import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Select, Space, Table, Tag, Typography } from 'antd';

import {
  ResourceRequestConfirmation,
  ResourceRequestPreview,
  ResourceRequestPreviewResource,
} from '../interfaces/interfaces';

const NEW_REQUIREMENT = '__new__';

const roleText = (resource: ResourceRequestPreviewResource) =>
  [resource.resource_type, resource.region, resource.service_line, resource.function, resource.career_level]
    .filter(Boolean)
    .join(' · ');

const datesText = (resource: ResourceRequestPreviewResource) =>
  `${resource.start_date} → ${resource.end_date}`;

const monthIdentity = (key: string, startDate: string) => {
  const match = /^month(\d+)$/i.exec(key);
  if (!match) return { label: key, order: `9999-${key}` };
  const start = new Date(`${startDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return { label: key, order: `9999-${key}` };
  const month = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + Number(match[1]), 1));
  return {
    label: month.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    order: month.toISOString().slice(0, 7),
  };
};

const allocationChanges = (
  previous: ResourceRequestPreviewResource,
  current: ResourceRequestPreviewResource
) => {
  const calendarAllocation = (resource: ResourceRequestPreviewResource) =>
    Object.fromEntries(
      Object.entries(resource.monthly_wise_allocation ?? {}).map(([key, value]) => {
        const identity = monthIdentity(key, resource.start_date);
        return [identity.label, { value, order: identity.order }];
      })
    );
  const oldAllocation = calendarAllocation(previous);
  const newAllocation = calendarAllocation(current);
  return Array.from(new Set([...Object.keys(oldAllocation), ...Object.keys(newAllocation)]))
    .sort((a, b) => (oldAllocation[a]?.order ?? newAllocation[a]?.order ?? a)
      .localeCompare(oldAllocation[b]?.order ?? newAllocation[b]?.order ?? b))
    .flatMap((month) => {
      const oldEntry = oldAllocation[month];
      const newEntry = newAllocation[month];
      const oldValue = oldEntry?.value;
      const newValue = newEntry?.value;
      if (oldEntry && newEntry && Number(oldValue) === Number(newValue)) return [];
      return [{
        month,
        monthLabel: month,
        previous: oldValue,
        current: newValue,
        delta: Number(newValue ?? 0) - Number(oldValue ?? 0),
      }];
    });
};

const changedLabels = (
  previous: ResourceRequestPreviewResource,
  current: ResourceRequestPreviewResource
) => {
  const fields: Array<[keyof ResourceRequestPreviewResource, string]> = [
    ['resource_type', 'resource type'],
    ['region', 'region'],
    ['service_line', 'service line'],
    ['function', 'function'],
    ['career_level', 'level'],
    ['start_date', 'start date'],
    ['end_date', 'end date'],
  ];
  const changed = fields.filter(([field]) => previous[field] !== current[field]).map(([, label]) => label);
  if (allocationChanges(previous, current).length > 0) changed.push('monthly allocation');
  return changed;
};

interface Props {
  open: boolean;
  preview: ResourceRequestPreview | null;
  confirmLoading?: boolean;
  onCancel: () => void;
  onConfirm: (confirmation: ResourceRequestConfirmation) => void;
}

const ResourceRequestMappingModal: React.FC<Props> = ({
  open,
  preview,
  confirmLoading = false,
  onCancel,
  onConfirm,
}) => {
  const [mapping, setMapping] = useState<Record<number, string | undefined>>({});
  const [showUnchanged, setShowUnchanged] = useState(false);

  useEffect(() => {
    if (!preview) return;
    const suggestions = new Map(
      preview.suggested_mappings.map((item) => [item.current_key, item])
    );
    setMapping(
      Object.fromEntries(
        preview.current_resources.map((resource) => {
          const suggestion = suggestions.get(resource.key);
          if (resource.ambiguous) return [resource.key, undefined];
          return [
            resource.key,
            suggestion?.action === 'continue' && suggestion.previous_resource_requirement_id
              ? suggestion.previous_resource_requirement_id
              : NEW_REQUIREMENT,
          ];
        })
      )
    );
    setShowUnchanged(false);
  }, [preview]);

  const previousById = useMemo(
    () => new Map(
      (preview?.previous_resources ?? []).map((resource) => [String(resource.resource_requirement_id), resource])
    ),
    [preview]
  );
  const usedPreviousIds = new Set(
    Object.values(mapping).filter((value): value is string => Boolean(value && value !== NEW_REQUIREMENT))
  );
  const removed = (preview?.previous_resources ?? []).filter(
    (resource) => !usedPreviousIds.has(String(resource.resource_requirement_id))
  );
  const blockedChanged = (preview?.current_resources ?? []).filter((resource) => {
    const previous = mapping[resource.key] ? previousById.get(String(mapping[resource.key])) : undefined;
    return Boolean(previous?.has_bookings && previous.resource_fingerprint !== resource.resource_fingerprint);
  });
  const blockedRemoved = removed.filter((resource) => resource.has_bookings);
  const missingMappings = (preview?.current_resources ?? []).some((resource) => !mapping[resource.key]);
  const blocked = blockedChanged.length > 0 || blockedRemoved.length > 0;
  const visibleResources = (preview?.current_resources ?? []).filter((resource) => {
    if (showUnchanged || resource.ambiguous) return true;
    const previous = mapping[resource.key] ? previousById.get(String(mapping[resource.key])) : undefined;
    return !previous || previous.resource_fingerprint !== resource.resource_fingerprint;
  });

  const columns = [
    {
      title: 'Current requirement',
      key: 'current',
      render: (_: unknown, resource: ResourceRequestPreviewResource) => (
        <div>
          <Typography.Text strong>{roleText(resource)}</Typography.Text>
          <br />
          <Typography.Text type="secondary">{datesText(resource)}</Typography.Text>
          {resource.ambiguous && <><br /><Tag color="orange">Manual match required</Tag></>}
        </div>
      ),
    },
    {
      title: 'Continue from previous requirement',
      key: 'mapping',
      width: 390,
      render: (_: unknown, resource: ResourceRequestPreviewResource) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Select a previous requirement or mark as new"
          value={mapping[resource.key]}
          onChange={(value) => setMapping((current) => ({ ...current, [resource.key]: value }))}
          options={[
            { value: NEW_REQUIREMENT, label: 'Treat as a new requirement' },
            ...(preview?.previous_resources ?? []).map((previous) => {
              const id = String(previous.resource_requirement_id);
              const usedElsewhere = usedPreviousIds.has(id) && mapping[resource.key] !== id;
              return {
                value: id,
                disabled: usedElsewhere,
                label: `${roleText(previous)} · ${datesText(previous)}${previous.workflow_stage ? ` · ${previous.workflow_stage}` : ''}`,
              };
            }),
          ]}
        />
      ),
    },
    {
      title: 'Result',
      key: 'result',
      width: 210,
      render: (_: unknown, resource: ResourceRequestPreviewResource) => {
        const selected = mapping[resource.key];
        if (!selected) return <Tag color="orange">Select a match</Tag>;
        if (selected === NEW_REQUIREMENT) return <Tag color="blue">New</Tag>;
        const previous = previousById.get(selected);
        const unchanged = previous?.resource_fingerprint === resource.resource_fingerprint;
        const changes = previous && !unchanged ? changedLabels(previous, resource) : [];
        return (
          <div>
            <Tag color={unchanged ? 'green' : previous?.has_bookings ? 'red' : 'gold'}>
              {unchanged ? 'Unchanged' : 'Changed'}
            </Tag>
            {previous?.workflow_stage && <Tag>{previous.workflow_stage}</Tag>}
            {changes.length > 0 && (
              <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                Changed: {changes.join(', ')}
              </Typography.Text>
            )}
          </div>
        );
      },
    },
  ];

  const submit = () => {
    if (!preview || missingMappings || blocked) return;
    onConfirm({
      preview_token: preview.preview_token,
      mappings: preview.current_resources.map((resource) => {
        const selected = mapping[resource.key]!;
        return selected === NEW_REQUIREMENT
          ? { current_key: resource.key, action: 'new', previous_resource_requirement_id: null }
          : { current_key: resource.key, action: 'continue', previous_resource_requirement_id: selected };
      }),
    });
  };

  return (
    <Modal
      title="Review resource-request changes"
      open={open}
      width="92%"
      okText="Confirm mapping and send request"
      cancelText="Back to pricing"
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: missingMappings || blocked }}
      onOk={submit}
      onCancel={onCancel}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message="Review every resource requirement before the request is saved and sent."
        description={preview?.previous_version_name
          ? `Compared with the last deal-wide resource request: ${preview.previous_version_name}.`
          : 'This is the first resource request for the deal.'}
        style={{ marginBottom: 16 }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Space size={[4, 8]} wrap>
          <Tag color="gold">{preview?.summary?.changed ?? 0} changed</Tag>
          <Tag color="blue">{preview?.summary?.added ?? 0} added</Tag>
          <Tag color="red">{removed.length} removed</Tag>
          {(preview?.summary?.ambiguous ?? 0) > 0 && (
            <Tag color="orange">{preview?.summary?.ambiguous} need manual matching</Tag>
          )}
          <Tag color="green">{preview?.summary?.unchanged ?? 0} unchanged</Tag>
        </Space>
        {(preview?.summary?.unchanged ?? 0) > 0 && (
          <Button type="link" onClick={() => setShowUnchanged((value) => !value)}>
            {showUnchanged ? 'Hide unchanged requirements' : 'Show unchanged requirements'}
          </Button>
        )}
      </div>
      {blocked && (
        <Alert
          type="error"
          showIcon
          message="A committed or booked requirement would be changed or removed."
          description="Adjust or cancel its booking in Smart Profiler/OpenAir before sending this resource request."
          style={{ marginBottom: 16 }}
        />
      )}
      <Table
        rowKey="key"
        pagination={false}
        dataSource={visibleResources}
        columns={columns}
        scroll={{ x: 1000 }}
        expandable={{
          rowExpandable: (resource) => {
            const previous = mapping[resource.key]
              ? previousById.get(String(mapping[resource.key]))
              : undefined;
            return Boolean(previous && allocationChanges(previous, resource).length);
          },
          expandedRowRender: (resource) => {
            const previous = previousById.get(String(mapping[resource.key]));
            if (!previous) return null;
            const changes = allocationChanges(previous, resource);
            return (
              <div style={{ padding: '4px 12px 12px' }}>
                <Typography.Text strong>Monthly allocation changes</Typography.Text>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="month"
                  style={{ marginTop: 8 }}
                  dataSource={changes}
                  columns={[
                    { title: 'Month', dataIndex: 'monthLabel' },
                    {
                      title: 'Previous',
                      dataIndex: 'previous',
                      render: (value?: number) => value == null ? '—' : `${value}%`,
                    },
                    {
                      title: 'Current',
                      dataIndex: 'current',
                      render: (value?: number) => value == null ? '—' : `${value}%`,
                    },
                    {
                      title: 'Difference',
                      dataIndex: 'delta',
                      render: (value: number) => (
                        <Tag color={value > 0 ? 'green' : 'red'}>{value > 0 ? '+' : ''}{value}%</Tag>
                      ),
                    },
                  ]}
                />
              </div>
            );
          },
        }}
      />
      {removed.length > 0 && (
        <Alert
          type={blockedRemoved.length ? 'error' : 'warning'}
          showIcon
          message={`${removed.length} previous requirement${removed.length === 1 ? '' : 's'} will be removed`}
          description={removed.map(roleText).join('; ')}
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default ResourceRequestMappingModal;
